/**
 * FlightPlansList - Saved flight plans management panel
 * Shows saved flight plans with search, filtering, sorting, and pagination
 * to handle large numbers of plans without excessive scrolling.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
	LuPencil,
	LuTrash,
	LuCamera,
	LuRefreshCw,
	LuLoader,
	LuExternalLink,
	LuSearch,
	LuChevronLeft,
	LuChevronRight,
} from 'react-icons/lu';
import { getFlightPlans, updateFlightPlan, deleteFlightPlan } from '@utils/api/forestryFlight';

// ─── Constants ────────────────────────────────────────────────────────────────

const PLANS_PER_PAGE = 5;

const STATUS_LABELS = {
	draft: 'Draft',
	planned: 'Planned',
	saved: 'Planned',
	exported: 'Exported',
	flight_completed: 'Flight Done',
	completed: 'Flight Done',
	syncing: 'Syncing…',
	synced: 'Synced',
	sync_failed: 'Sync Failed',
	processing: 'Processing…',
	processed: 'Results Ready',
};

const STATUS_GROUPS = {
	active: ['syncing', 'processing'],
	planned: ['draft', 'planned', 'saved', 'exported'],
	completed: ['flight_completed', 'completed', 'synced', 'processed'],
	failed: ['sync_failed'],
};

const STATUS_ORDER = Object.fromEntries([
	...STATUS_GROUPS.active.map(s => [s, 0]),
	...STATUS_GROUPS.planned.map(s => [s, 1]),
	...STATUS_GROUPS.completed.map(s => [s, 2]),
	...STATUS_GROUPS.failed.map(s => [s, 3]),
]);

const SORT_OPTIONS = [
	{ value: 'newest', label: 'Newest first' },
	{ value: 'oldest', label: 'Oldest first' },
	{ value: 'name', label: 'Name A–Z' },
	{ value: 'status', label: 'Status' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = dateStr => {
	if (!dateStr) return '';
	try {
		const d = new Date(dateStr);
		return d.toLocaleDateString('nb-NO', {
			day: 'numeric',
			month: 'short',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return '';
	}
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const SearchRow = styled.div`
	position: relative;
	display: flex;
	align-items: center;
`;

const SearchInput = styled.input`
	width: 100%;
	padding: 0.4rem 0.5rem 0.4rem 1.8rem;
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 6px;
	background: rgba(10, 14, 20, 0.5);
	color: #fff;
	font-size: 0.8rem;
	font-family: inherit;
	transition: border-color 0.2s ease;

	&:focus {
		outline: none;
		border-color: rgba(52, 211, 153, 0.4);
	}

	&::placeholder {
		color: rgba(148, 163, 184, 0.4);
	}
`;

const SearchIcon = styled(LuSearch)`
	position: absolute;
	left: 0.55rem;
	color: rgba(148, 163, 184, 0.5);
	pointer-events: none;
`;

const ControlsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	flex-wrap: nowrap;
`;

const FilterChip = styled.button`
	background: ${props =>
		props.$active ? 'rgba(52, 211, 153, 0.12)' : 'rgba(255, 255, 255, 0.03)'};
	border: 1px solid
		${props => (props.$active ? 'rgba(52, 211, 153, 0.3)' : 'rgba(255, 255, 255, 0.06)')};
	color: ${props => (props.$active ? props.theme.colors.green : '#888')};
	font-size: 0.65rem;
	padding: 0.15rem 0.3rem;
	border-radius: 3px;
	cursor: pointer;
	transition: all 0.15s ease;
	white-space: nowrap;
	font-family: inherit;

	&:hover {
		border-color: rgba(52, 211, 153, 0.25);
		color: ${props => props.theme.colors.green};
	}
`;

const SortSelect = styled.select`
	background: rgba(10, 14, 20, 0.5);
	border: 1px solid rgba(255, 255, 255, 0.06);
	color: #888;
	font-size: 0.65rem;
	padding: 0.15rem 0.25rem;
	border-radius: 3px;
	cursor: pointer;
	transition: all 0.15s ease;
	font-family: inherit;

	&:hover,
	&:focus {
		border-color: rgba(52, 211, 153, 0.25);
		color: ${props => props.theme.colors.green};
		outline: none;
	}

	option {
		background: #0e1218;
		color: #ccc;
		font-size: 0.65rem;
	}
`;

const PlanCard = styled.div`
	background: rgba(255, 255, 255, 0.03);
	border: 1px solid
		${props => (props.$active ? 'rgba(52, 211, 153, 0.4)' : 'rgba(255, 255, 255, 0.06)')};
	border-radius: 8px;
	padding: 0.6rem 0.75rem;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		border-color: rgba(52, 211, 153, 0.25);
		background: rgba(52, 211, 153, 0.04);
	}
`;

const PlanHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
`;

const PlanName = styled.span`
	font-size: 0.85rem;
	font-weight: 500;
	color: ${props => props.theme.colors.textColor};
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const PlanMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 0.4rem;
	margin-top: 0.3rem;
	font-size: 0.7rem;
	color: ${props => props.theme.colors.textAccent};
`;

const ActionBtnGroup = styled.div`
	display: flex;
	gap: 0.15rem;
`;

const spin = keyframes`
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
`;

const pulse = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.5; transform: scale(1.4); }
`;

const StatusDot = styled.span`
	display: inline-block;
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: ${props => {
		switch (props.$status) {
			case 'planned':
				return '#2196F3';
			case 'saved':
				return '#2196F3';
			case 'exported':
				return '#9C27B0';
			case 'flight_completed':
				return '#4CAF50';
			case 'completed':
				return '#4CAF50';
			case 'syncing':
				return '#FF9800';
			case 'synced':
				return '#00BCD4';
			case 'sync_failed':
				return '#F44336';
			case 'processing':
				return '#FF9800';
			case 'processed':
				return '#4CAF50';
			default:
				return '#9E9E9E';
		}
	}};
	${props =>
		props.$status === 'syncing' &&
		css`
			animation: ${pulse} 1.5s ease-in-out infinite;
		`}
`;

const SpinnerIcon = styled(LuLoader)`
	animation: ${spin} 1s linear infinite;
	color: #ff9800;
`;

const ResultsLink = styled.a`
	font-size: 0.65rem;
	color: #4caf50;
	text-decoration: none;
	display: inline-flex;
	align-items: center;
	gap: 0.2rem;
	opacity: 0.8;
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 1;
		text-decoration: underline;
	}
`;

const TypeBadge = styled.span`
	font-size: 0.65rem;
	padding: 0.1rem 0.35rem;
	border-radius: 3px;
	background: ${props =>
		props.$type === 'dsp' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(33, 150, 243, 0.15)'};
	color: ${props => (props.$type === 'dsp' ? '#CE93D8' : '#90CAF9')};
	text-transform: uppercase;
	font-weight: 600;
`;

const ActionBtn = styled.button`
	background: none;
	border: none;
	color: ${props => (props.$danger ? '#f87171' : props.theme.colors.textAccent)};
	cursor: pointer;
	padding: 0.15rem 0.3rem;
	font-size: 0.8rem;
	border-radius: 4px;
	opacity: 0.6;
	transition: all 0.15s ease;

	&:hover {
		opacity: 1;
		background: ${props =>
			props.$danger ? 'rgba(248, 113, 113, 0.1)' : 'rgba(255, 255, 255, 0.08)'};
	}
`;

const RenameInput = styled.input`
	width: 100%;
	padding: 0.3rem 0.5rem;
	border: 1px solid rgba(52, 211, 153, 0.3);
	border-radius: 4px;
	background: rgba(10, 14, 20, 0.6);
	color: #fff;
	font-size: 0.85rem;
	font-family: inherit;

	&:focus {
		outline: none;
		border-color: rgba(52, 211, 153, 0.6);
	}
`;

const EmptyState = styled.div`
	text-align: center;
	padding: 1rem;
	color: ${props => props.theme.colors.textAccent};
	font-size: 0.8rem;
	opacity: 0.7;
`;

const RefreshBtn = styled.button`
	background: none;
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: ${props => props.theme.colors.textAccent};
	cursor: pointer;
	padding: 0.25rem 0.5rem;
	font-size: 0.75rem;
	border-radius: 6px;
	transition: all 0.15s ease;

	&:hover {
		border-color: rgba(52, 211, 153, 0.3);
		color: ${props => props.theme.colors.green};
	}
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 0.25rem;
`;

const PlanCount = styled.span`
	font-size: 0.7rem;
	color: #888;
`;

const ConfirmOverlay = styled.div`
	margin-top: 0.4rem;
	padding: 0.5rem;
	background: rgba(248, 113, 113, 0.08);
	border: 1px solid rgba(248, 113, 113, 0.2);
	border-radius: 6px;
	font-size: 0.8rem;
	color: #fca5a5;
`;

const ConfirmActions = styled.div`
	display: flex;
	gap: 0.5rem;
	margin-top: 0.3rem;
`;

const FlightResultsMeta = styled(PlanMeta)`
	margin-top: 0.2rem;
`;

const PaginationRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.4rem 0 0.1rem;
`;

const PageButton = styled.button`
	background: ${props =>
		props.$active ? 'rgba(52, 211, 153, 0.15)' : 'rgba(255, 255, 255, 0.03)'};
	border: 1px solid
		${props => (props.$active ? 'rgba(52, 211, 153, 0.3)' : 'rgba(255, 255, 255, 0.06)')};
	color: ${props => (props.$active ? props.theme.colors.green : '#888')};
	font-size: 0.7rem;
	width: 24px;
	height: 24px;
	border-radius: 4px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s ease;

	&:hover:not(:disabled) {
		border-color: rgba(52, 211, 153, 0.3);
		color: ${props => props.theme.colors.green};
	}

	&:disabled {
		opacity: 0.3;
		cursor: default;
	}
`;

const PageInfo = styled.span`
	font-size: 0.65rem;
	color: #666;
`;

const ErrorText = styled.div`
	font-size: 0.8rem;
	color: #f87171;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const FlightPlansList = ({ onLoadPlan, activePlanId }) => {
	const [plans, setPlans] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [renamingId, setRenamingId] = useState(null);
	const [renameText, setRenameText] = useState('');
	const [deletingId, setDeletingId] = useState(null);

	// Search, filter, sort, pagination state
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [sortBy, setSortBy] = useState('newest');
	const [currentPage, setCurrentPage] = useState(1);

	const searchTimeoutRef = useRef(null);
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const isMountedRef = useRef(true);
	const isInitialLoadRef = useRef(true);
	const renamingIdRef = useRef(null);

	// Keep ref in sync with state for blur handler guard
	useEffect(() => {
		renamingIdRef.current = renamingId;
	}, [renamingId]);

	// Cleanup debounce timeout on unmount
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			clearTimeout(searchTimeoutRef.current);
		};
	}, []);

	// Debounce search input
	const handleSearchChange = useCallback(e => {
		const value = e.target.value;
		setSearchQuery(value);

		clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => {
			setDebouncedSearch(value);
			setCurrentPage(1);
		}, 200);
	}, []);

	const handleFilterChange = useCallback(filter => {
		setStatusFilter(prev => (prev === filter ? 'all' : filter));
		setCurrentPage(1);
	}, []);

	const handleSortChange = useCallback(e => {
		setSortBy(e.target.value);
	}, []);

	// Memoized filtered + sorted plans
	const processedPlans = useMemo(() => {
		let result = [...plans];

		// Filter by search query
		if (debouncedSearch) {
			const query = debouncedSearch.toLowerCase();
			result = result.filter(
				plan =>
					plan.name?.toLowerCase().includes(query) ||
					plan.flight_type?.toLowerCase().includes(query) ||
					(STATUS_LABELS[plan.status] || plan.status)?.toLowerCase().includes(query)
			);
		}

		// Filter by status group
		if (statusFilter !== 'all') {
			const allowedStatuses = STATUS_GROUPS[statusFilter] || [];
			result = result.filter(plan => allowedStatuses.includes(plan.status));
		}

		// Sort
		switch (sortBy) {
			case 'newest':
			case 'oldest': {
				const dir = sortBy === 'newest' ? -1 : 1;
				result.sort((a, b) => {
					const tsA = new Date(a.updated_at || a.created_at).getTime();
					const tsB = new Date(b.updated_at || b.created_at).getTime();
					return (tsA - tsB) * dir;
				});
				break;
			}
			case 'name':
				result.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nb-NO'));
				break;
			case 'status':
				result.sort(
					(a, b) => (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4)
				);
				break;
			default:
				break;
		}

		return result;
	}, [plans, debouncedSearch, statusFilter, sortBy]);

	// Pagination — derived values, no effect needed
	const totalPages = Math.max(1, Math.ceil(processedPlans.length / PLANS_PER_PAGE));
	const safePage = Math.min(currentPage, totalPages);
	const paginatedPlans = useMemo(() => {
		const start = (safePage - 1) * PLANS_PER_PAGE;
		return processedPlans.slice(start, start + PLANS_PER_PAGE);
	}, [processedPlans, safePage]);

	const fetchPlans = useCallback(async (isPolling = false) => {
		// Only show loading spinner on initial load, not on background polls
		if (!isPolling) {
			setLoading(true);
		}
		setError(null);
		try {
			const data = await getFlightPlans();
			if (isMountedRef.current) {
				setPlans(data);
				isInitialLoadRef.current = false;
			}
		} catch (err) {
			if (isMountedRef.current) {
				console.error('Failed to fetch flight plans:', err);
				// Only show error on initial load, not on poll failures
				if (!isPolling) {
					setError('Failed to load plans');
				}
			}
		} finally {
			if (isMountedRef.current && !isPolling) {
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		fetchPlans(false);
		const interval = setInterval(() => fetchPlans(true), 30000);
		return () => clearInterval(interval);
	}, [fetchPlans]);

	const handleRename = async planId => {
		// Guard: don't rename if user already cancelled (Escape → blur race)
		if (renamingIdRef.current !== planId) return;

		const trimmed = renameText.trim();
		if (!trimmed) return;
		try {
			await updateFlightPlan(planId, { name: trimmed });
			setPlans(prev => prev.map(p => (p.id === planId ? { ...p, name: trimmed } : p)));
			setRenamingId(null);
		} catch (err) {
			console.error('Rename failed:', err);
		}
	};

	const handleDelete = async planId => {
		try {
			await deleteFlightPlan(planId);
			setPlans(prev => prev.filter(p => p.id !== planId));
			setDeletingId(null);
		} catch (err) {
			console.error('Delete failed:', err);
		}
	};

	// Only show controls when there are enough plans to warrant them
	const showControls = plans.length > 3;

	return (
		<Container>
			<HeaderRow>
				<PlanCount>
					{processedPlans.length}
					{processedPlans.length !== plans.length ? ` / ${plans.length}` : ''} plan
					{plans.length !== 1 ? 's' : ''}
				</PlanCount>
				<RefreshBtn
					onClick={() => fetchPlans(false)}
					disabled={loading}
					aria-label="Refresh plans">
					{loading ? <LuLoader size={12} /> : <LuRefreshCw size={12} />} Refresh
				</RefreshBtn>
			</HeaderRow>

			{showControls && (
				<>
					<SearchRow>
						<SearchIcon size={13} />
						<SearchInput
							type="search"
							placeholder="Search plans…"
							value={searchQuery}
							onChange={handleSearchChange}
							aria-label="Search flight plans"
						/>
					</SearchRow>

					<ControlsRow>
						<FilterChip
							$active={statusFilter === 'active'}
							onClick={() => handleFilterChange('active')}
							aria-pressed={statusFilter === 'active'}>
							Active
						</FilterChip>
						<FilterChip
							$active={statusFilter === 'planned'}
							onClick={() => handleFilterChange('planned')}
							aria-pressed={statusFilter === 'planned'}>
							Pending
						</FilterChip>
						<FilterChip
							$active={statusFilter === 'completed'}
							onClick={() => handleFilterChange('completed')}
							aria-pressed={statusFilter === 'completed'}>
							Done
						</FilterChip>
						<FilterChip
							$active={statusFilter === 'failed'}
							onClick={() => handleFilterChange('failed')}
							aria-pressed={statusFilter === 'failed'}>
							Failed
						</FilterChip>
					</ControlsRow>

					<SortSelect value={sortBy} onChange={handleSortChange} aria-label="Sort plans">
						{SORT_OPTIONS.map(opt => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</SortSelect>
				</>
			)}

			{error && <ErrorText>{error}</ErrorText>}

			{!loading && !error && plans.length === 0 && (
				<EmptyState>
					No saved flight plans yet.
					<br />
					Create one using the workflow above.
				</EmptyState>
			)}

			{!loading && plans.length > 0 && processedPlans.length === 0 && (
				<EmptyState>No plans match your filters.</EmptyState>
			)}

			{paginatedPlans.map(plan => (
				<PlanCard
					key={plan.id}
					$active={activePlanId === plan.id}
					onClick={() => onLoadPlan && onLoadPlan(plan)}>
					<PlanHeader>
						{renamingId === plan.id ? (
							<RenameInput
								value={renameText}
								onChange={e => setRenameText(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter') handleRename(plan.id);
									if (e.key === 'Escape') setRenamingId(null);
								}}
								onBlur={() => handleRename(plan.id)}
								autoFocus
								aria-label="Rename plan"
							/>
						) : (
							<>
								<PlanName>{plan.name}</PlanName>
								<ActionBtnGroup>
									<ActionBtn
										title="Rename"
										aria-label={`Rename ${plan.name}`}
										onClick={e => {
											e.stopPropagation();
											setRenamingId(plan.id);
											setRenameText(plan.name);
										}}>
										<LuPencil size={14} />
									</ActionBtn>
									<ActionBtn
										title="Delete"
										aria-label={`Delete ${plan.name}`}
										$danger
										onClick={e => {
											e.stopPropagation();
											setDeletingId(plan.id);
										}}>
										<LuTrash size={14} />
									</ActionBtn>
								</ActionBtnGroup>
							</>
						)}
					</PlanHeader>
					<PlanMeta>
						<TypeBadge $type={plan.flight_type}>
							{plan.flight_type === 'dsp' ? 'DSP' : 'Ortho'}
						</TypeBadge>
						{plan.status === 'processing' ? (
							<SpinnerIcon size={12} data-testid="status-spinner" />
						) : (
							<StatusDot $status={plan.status} data-testid="status-dot" />
						)}
						<span>{STATUS_LABELS[plan.status] || plan.status}</span>
						{plan.status === 'processed' && plan.project_id && (
							<ResultsLink
								href={`/projects/${plan.project_id}`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={e => e.stopPropagation()}
								data-testid="view-results-link">
								<LuExternalLink size={10} /> View Results
							</ResultsLink>
						)}
						<span>·</span>
						<span>{formatDate(plan.updated_at || plan.created_at)}</span>
					</PlanMeta>

					{deletingId === plan.id && (
						<ConfirmOverlay>
							Delete &ldquo;{plan.name}&rdquo;?
							<ConfirmActions>
								<ActionBtn
									$danger
									onClick={e => {
										e.stopPropagation();
										handleDelete(plan.id);
									}}>
									Yes, delete
								</ActionBtn>
								<ActionBtn
									onClick={e => {
										e.stopPropagation();
										setDeletingId(null);
									}}>
									Cancel
								</ActionBtn>
							</ConfirmActions>
						</ConfirmOverlay>
					)}

					{plan.flight_results && (
						<FlightResultsMeta>
							<span>
								<LuCamera size={12} style={{ verticalAlign: 'middle' }} />{' '}
								{plan.flight_results.images_captured} images
							</span>
							<span>·</span>
							<span>
								{plan.flight_results.waypoints_completed}/
								{plan.flight_results.total_waypoints} wp
							</span>
						</FlightResultsMeta>
					)}
				</PlanCard>
			))}

			{totalPages > 1 && (
				<PaginationRow>
					<PageButton
						onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
						disabled={safePage <= 1}
						aria-label="Previous page">
						<LuChevronLeft size={12} />
					</PageButton>
					{Array.from({ length: totalPages }, (_, i) => i + 1)
						.filter(page => {
							if (totalPages <= 5) return true;
							if (page === 1 || page === totalPages) return true;
							if (Math.abs(page - safePage) <= 1) return true;
							return false;
						})
						.reduce((acc, page, idx, arr) => {
							if (idx > 0 && page - arr[idx - 1] > 1) {
								acc.push({ type: 'ellipsis', key: `e-${page}` });
							}
							acc.push({ type: 'page', page, key: `p-${page}` });
							return acc;
						}, [])
						.map(item =>
							item.type === 'ellipsis' ? (
								<PageInfo key={item.key}>…</PageInfo>
							) : (
								<PageButton
									key={item.key}
									$active={safePage === item.page}
									onClick={() => setCurrentPage(item.page)}
									aria-label={`Page ${item.page}`}
									aria-current={safePage === item.page ? 'page' : undefined}>
									{item.page}
								</PageButton>
							)
						)}
					<PageButton
						onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
						disabled={safePage >= totalPages}
						aria-label="Next page">
						<LuChevronRight size={12} />
					</PageButton>
				</PaginationRow>
			)}
		</Container>
	);
};

export default FlightPlansList;
